import { useCallback, useEffect, useMemo, useState } from "react";
import { buildStyles, CircularProgressbar } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import { BsVolumeMuteFill, BsVolumeOffFill, BsVolumeUpFill } from "react-icons/bs";
import { IoMdArrowDown, IoMdArrowDropdown, IoMdArrowDropup, IoMdClose, IoMdDownload } from "react-icons/io";
import { RiPauseLine, RiPlayFill } from "react-icons/ri";
import {
  Bpm,
  downloadStem,
  downloadTrack,
  keyToString,
  Pack,
  Track,
  UITrack,
  updateTrack,
} from "../../firebase/firestore";
import { useAuth } from "../../hooks/useAuth";
import { useObjectViews } from "../../hooks/useObjectViews";
import { useTrackPlayer } from "../../hooks/useTrackPlayer";
import { FirestoreTrack, Key, Stem, stringToKey, UIStem } from "../../hooks/useUpload";
import { useScreenSize } from "../../styles/media-queries";
import { prettyDate } from "../../utils/dates";
import Circle from "../misc/Circle";
import SpotLoader from "../misc/SpotLoader";
import { SmallInlineEditable } from "../SmallInlineEditable";
import { DownloadButton } from "./DownloadButton";
import { TrackRowCompact, TrackRowProps } from "./TrackRowCompact";
import Waveform from "./Waveform";

const fuzzysort = require("fuzzysort");
import tags from "./tags.json";
import TrackTags from "./TrackTags";

function Bpm({ editable, bpm, track, setBpm, ...rest }) {
  const options = useMemo(() => {
    let res = [];
    if (track?.file?.bpm) res.push({ subtitle: "filename", value: track.file.bpm });
    if (track?.audio?.bpm) res.push({ subtitle: "audio", value: track.audio.bpm });
    return res;
  }, [track]);
  return (
    <SmallInlineEditable
      placeholderWidth={36}
      editable={editable}
      type="number"
      options={options}
      maxLength={3}
      value={bpm?.value}
      setValue={(newValue: number) => setBpm({ source: "custom", value: newValue })}
      placeholder={"Enter"}
      {...rest}
      label="bpm"
    />
  );
}

function Key({ editable, kkey, track, setKey, ...rest }: any & { key: Key }) {
  const options = useMemo(() => {
    let res = [];
    if (track?.file?.key) res.push({ subtitle: "from filename", value: keyToString(track.file.key) });
    if (track?.audio?.key) res.push({ subtitle: "from audio", value: keyToString(track.audio.key) });
    return res;
  }, [track]);
  return (
    <SmallInlineEditable
      editable={editable}
      type="text"
      placeholderWidth={62}
      maxLength={3}
      options={options}
      value={kkey && keyToString(kkey.value)}
      setValue={(keyString: string) => setKey({ source: "custom", value: stringToKey(keyString) })}
      placeholder={"Enter key"}
      {...rest}
    />
  );
}

export function TrackRow({
  uiTrack,
  uploadProgress,
  actions,
  onDelete,
  selected,
  onDeselect,
  onSelect,
  selectable,
  editable,
  className,
  playable,
  downloadable,
  creator,
  idx,
  showCreator,
  pack,
  compact = false,
  ...rest
}: TrackRowProps) {
  const uploading = uploadProgress !== undefined && uploadProgress !== 100;
  const { track } = uiTrack;
  const { user } = useAuth();

  const { playTrack, currentTrack, pause, playing, percentComplete, goToPosition, loading } = useTrackPlayer();

  const trackPlaying: boolean = !!(currentTrack?.id === track?.id && playing);

  // todo:
  const setBpm = (newBpm: Bpm) => updateTrack(track.path, { bpm: newBpm });
  const setKey = (newKey: Key) => updateTrack(track.path, { key: newKey });
  const setTitle = (newTitle: string) => newTitle && updateTrack(track.path, { title: newTitle });

  const { phone } = useScreenSize();

  const showOverlayView = phone && !editable;
  const showDate = compact && !phone;

  const onDownload = useCallback(async () => {
    //@ts-ignore
    if (!uiTrack.ref) throw new Error(`trying to download a non-firestore track`);
    //@ts-ignore
    let firestoreTrack: FirestoreTrack = uiTrack;

    await downloadTrack(firestoreTrack, track?.creator || creator, user, pack, pack?.creator);
  }, [creator, uiTrack]);

  const onDownloadStem = useCallback(async (stem) => {
    //@ts-ignore
    if (!uiTrack.ref) throw new Error(`trying to download a non-firestore track`);
    //@ts-ignore
    let firestoreTrack: FirestoreTrack = uiTrack;

    return await downloadStem(firestoreTrack, stem, creator);
  }, []);

  const toggleStemMute = (stem: UIStem) => {
    setStems(stems.map((s) => (s.storagePath === stem.storagePath ? { ...s, muted: !stem.muted } : s)));
  };

  const [stems, setStems] = useState<UIStem[]>(null);
  useEffect(() => setStems(track.stems), [track?.stems]);
  const [showStems, setShowStems] = useState(false);
  const trackLastPlayed = currentTrack?.id === track?.id;
  const thisTrackPercentCompleted = useMemo(
    () => (trackPlaying ? percentComplete : thisTrackPercentCompleted || 0),
    [trackPlaying, percentComplete]
  );

  const [clickedPlay, setClickedPlay] = useState(false);
  const onWaveformClick = async (pos) => {
    if (trackPlaying) {
      goToPosition(pos);
    } else {
      await playTrack(track);
      await goToPosition(pos);
    }
  };

  const canDownload = downloadable || !uploading;

  const showWaveform = trackPlaying || (!editable && !track?.tags);
  const showWaveformOnHover = !editable && (track.tags || phone);

  const showAnalyzing = track?.title && track.analyze_status && track.analyze_status !== "done" && !track.waveform;

  const trackTags = (
    <TrackTags
      editable={editable}
      className={`${showWaveformOnHover && "group-hover:opacity-0 group-hover:pointer-events-none"} ${
        showWaveform && "opacity-0 pointer-events-none"
      } transition-opacity right-0 absolute`}
      uiTrack={uiTrack}
    />
  );
  return (
    <div>
      <div
        className={`${className} group ${phone && "hover:bg-white hover:bg-opacity-10"} ${
          compact ? "h-9 w-full text-sm" : "h-14  w-full"
        } relative transition  rounded  flex items-center my-1  ${uploading && "opacity-50"}`}
      >
        {/* {uploadProgress && <div className={"transition-all z-0 absolute top-0 left-0 h-full bg-slate-400"} style={{ width: `${uploadProgress}%` }}></div>} */}

        <div
          className={`${!compact && (!phone || showOverlayView) && "relative"} ${
            showOverlayView ? "" : ""
          }  z-20 h-full flex items-center`}
        >
          {!phone && (
            <div
              className={`text-sm text-light-gray opacity-60 ${
                compact ? "absolute left-7" : "absolute right-2"
              } w-5 group-hover:opacity-0 transition-opacity text-center`}
            >
              {idx}
            </div>
          )}
          {track?.stems?.length ? (
            <div
              onClick={() => setShowStems(!showStems)}
              className={`mr-2 group-hover:opacity-100 transition-opacity cursor-pointer  ${
                trackPlaying ? "opacity-100" : "opacity-0"
              } `}
            >
              {showStems ? (
                <IoMdArrowDropup className={`${phone ? "w-8 h-8" : "w-5 h-5"}`} />
              ) : (
                <IoMdArrowDropdown className={`${phone ? "w-8 h-8" : "w-5 h-5"}`} />
              )}
            </div>
          ) : null}
          {playable && (
            <div
              onClick={() => (trackPlaying ? pause() : playTrack(track))}
              className={`mr-2 ${!uploading && "group-hover:opacity-100"} transition-opacity cursor-pointer  ${
                trackPlaying && !uploading ? "opacity-100" : "opacity-0"
              } `}
            >
              {currentTrack?.id === track?.id && loading ? (
                <SpotLoader className={`${phone ? "w-8 h-8" : "w-5 h-5"}`} />
              ) : trackPlaying ? (
                <RiPauseLine className={`${phone ? "w-8 h-8" : "w-5 h-5"}`} />
              ) : (
                <RiPlayFill className={`${phone ? "w-8 h-8" : "w-5 h-5"}`} />
              )}
            </div>
          )}
          {!phone && (!phone || !editable) && selectable && (
            <input
              type="checkbox"
              onChange={(e) => (selected ? onDeselect() : onSelect())}
              checked={selected}
              className={`group-hover:opacity-100 shrink-0 ${
                compact ? "w-5 h-5" : "w-5 h-5"
              } transition-opacity z-10 mr-2 ${selected ? "opacity-100" : "opacity-0"}`}
            />
          )}
        </div>

        <div
          className={` w-60 md:w-80 xl:w-96 ${!compact && "mt-2"} ${
            showOverlayView ? "group-hover:opacity-0 transition-opacity" : ""
          } ${showOverlayView && trackPlaying ? "opacity-0" : ""} `}
        >
          <div className="flex items-center">
            {<TrackTitle track={track} setTitle={setTitle} editable={editable} />}
            {showCreator && (
              <span className=" whitespace-nowrap  text-xs text-gray-400">prod. {track.creator.nickname}</span>
            )}
          </div>
          {!compact && track.title && (
            <div className="relative" style={{ height: 28 }}>
              <div className="absolute left-0 top-0 flex items-center text-sm text-light-gray">
                {(editable || track?.bpm?.value) && (
                  <Bpm editable={editable} bpm={track.bpm} setBpm={setBpm} track={track} />
                )}
                {(editable || (track?.key?.value && track?.bpm?.value)) && <Circle />}
                {(editable || track?.key?.value) && (
                  <Key editable={editable} kkey={track.key} setKey={setKey} track={track} />
                )}
              </div>
            </div>
          )}
        </div>

        <div className={`shrink w-12 ${!editable && "hidden"} md:block`}></div>
        {showDate && (
          <div
            className={` w-32 transition text-light-gray ${compact ? "text-xs" : "text-sm"}`}
            style={{ opacity: uploading ? 0 : 1 }}
          >
            {prettyDate(track.uploadDate.toDate ? track.uploadDate?.toDate() : track.uploadDate)}
          </div>
        )}

        <div className={`relative ${showOverlayView ? "grow w-4/5 h-1/2" : "h-1/3 w-52"} `}>
          {track.waveform && (
            <Waveform
              waveform={track.waveform}
              className={`absolute right-0 w-full h-full ${showWaveform ? "opacity-100" : "opacity-0 "} ${
                showWaveformOnHover ? "group-hover:opacity-100" : ""
              } ${!showWaveformOnHover && !showWaveform && "group-hover:pointer-events-none"} transition-opacity`}
              style={{ width: 220 }}
              trimEnd={!track?.stems?.length}
              percentPlayed={thisTrackPercentCompleted}
              setPercentPlayed={onWaveformClick}
            />
          )}
          {!showAnalyzing && trackTags}
          {!uploading && showAnalyzing && (
            <div className="right-0 flex items-center absolute">
              <div className="text-xs mr-1 text-light-gray">analyzing</div>
              <SpotLoader className={`h-4 w-4 `} />
            </div>
          )}
        </div>

        {TrackActions(compact, uploading, canDownload, onDownload, editable, onDelete, uploadProgress, uiTrack, pack)}
      </div>

      {showStems ? (
        <div className=" ">
          {stems.map((stem) => (
            <TrackStemRow
              thisTrackPercentCompleted={thisTrackPercentCompleted}
              canDownload={canDownload}
              onDownload={() => onDownloadStem(stem)}
              trackLastPlayed={trackLastPlayed}
              trackPlaying={trackPlaying}
              key={`track#${track.id}-stem#${stem.name}`}
              stem={stem}
              toggleMute={async () => await toggleStemMute(stem)}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

type TrackStemProps = {
  stem: UIStem;
  trackPlaying?: boolean;
  muted?: boolean;
  toggleMute: () => Promise<any>;
  trackLastPlayed: boolean;
  thisTrackPercentCompleted: number;
  canDownload?: boolean;
  onDownload?: () => Promise<any>;
};

export function TrackTitle({ track, setTitle, editable = false }) {
  if (editable && track?.title)
    return <SmallInlineEditable editable fontWidth={7.5} value={track.title} setValue={setTitle} />;
  else
    return (
      <div className={` ${track.title ? "text-white" : "text-light-gray opacity-60"} px-2`}>
        {track.title || "Deleted track"}{" "}
        {track?.stems?.length ? <span className="text-xs text-light-gray"> + {track.stems.length} stems</span> : null}
      </div>
    );
}

function TrackActions(
  compact: boolean,
  uploading: boolean,
  canDownload: boolean,
  onDownload: () => Promise<void>,
  editable: boolean,
  onDelete: () => any,
  uploadProgress: number,
  uiTrack: UITrack,
  pack: Pack
) {
  const track = uiTrack.track;

  const { phone } = useScreenSize();
  const { views, loading, viewsInPackAggregated } = useObjectViews(
    editable && !uploading && uiTrack?.ref,
    "track",
    pack?.id
  );

  return (
    <div className={`${phone ? "ml-1 " : compact ? " ml-4 " : " "} w-24 flex h-full items-center`}>
      {/* {!uploading && actions && <Actions actions={actions} style={{ right: -12 }} className="cursor-pointer absolute right-0 top-1/2 -translate-x-1/2 -translate-y-1/2 transition-opacity opacity-0 group-hover:opacity-100 "/>} */}

      {!uploading && canDownload && (
        <DownloadButton
          onDownload={onDownload}
          views={views}
          viewsInPack={viewsInPackAggregated}
          className={"cursor-pointer mx-1  group-hover:opacity-100"}
        />
      )}

      {!uploading && editable && (
        <IoMdClose
          width={18}
          size={18}
          onClick={onDelete}
          className={"cursor-pointer mx-1 opacity-0 group-hover:opacity-70 hover:opacity-100"}
        />
      )}

      {uploading && editable && (
        <div className="h-4 w-4 cursor-pointer relative mx-2 ">
          <IoMdClose
            width={12}
            size={12}
            onClick={onDelete}
            className={"absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100"}
          />
          <CircularProgressbar
            styles={buildStyles({
              trailColor: `transparent`,
              pathColor: "white",
            })}
            value={uploadProgress}
          />
        </div>
      )}
    </div>
  );
}

function TrackStemRow({
  stem,
  trackPlaying,
  trackLastPlayed,
  thisTrackPercentCompleted,
  canDownload,
  onDownload,
  toggleMute,
  ...rest
}: TrackStemProps) {
  return (
    <div
      className={`${
        stem.muted && "opacity-40"
      } text-sm h-10 px-8 py-2 w-full relative transition  rounded  flex items-center `}
    >
      <div className=" w-40 md:w-52 shrink-0">{stem.name}</div>

      <div className="hidden md:block shrink w-4"></div>

      {stem.waveform && (
        <Waveform
          waveform={stem.waveform}
          trimEnd={false}
          className={`w-52 h-1/2  ${trackPlaying ? "opacity-100" : "opacity-100"} ${
            trackLastPlayed ? "group-hover:opacity-100" : ""
          } transition-opacity`}
          style={{ width: 220 }}
          percentPlayed={thisTrackPercentCompleted}
        />
      )}

      {/* {(trackPlaying) && <div onClick={toggleMute} className={`${trackLastPlayed && "group-hover:opacity-100"} cursor-pointer opacity-50 hover:opacity-100`}>
      {!!stem.muted ? <BsVolumeMuteFill className="w-4 h-4" /> : <BsVolumeUpFill className="w-4 h-4" />}
    </div>} */}
      {canDownload && <DownloadButton onDownload={onDownload} />}
    </div>
  );
}
